import { useState } from "react";
import { Wand2, Sparkles, Loader2, Lightbulb, Copy, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useLocale } from "@/contexts/LocaleContext";

interface OptimizationResult {
  original: string;
  optimized: string;
  explanation: string;
  improvements: string[];
}

export function DorkOptimizer() {
  const { t } = useLocale();
  const [input, setInput] = useState("");
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [result, setResult] = useState<OptimizationResult | null>(null);

  const handleOptimize = async () => {
    if (!input.trim()) {
      toast({
        title: t("optimizer.input_required"),
        description: t("optimizer.input_required_desc"),
        variant: "destructive",
      });
      return;
    }

    setIsOptimizing(true);
    setResult(null);

    try {
      const response = await supabase.functions.invoke("optimize-dork", {
        body: { query: input.trim() },
      });

      if (response.error) throw new Error(response.error.message);
      setResult(response.data);
      
      toast({
        title: t("optimizer.success"),
        description: t("optimizer.success_desc"),
      });
    } catch (error) {
      console.error("Optimization error:", error);
      toast({
        title: t("optimizer.failed"),
        description: error instanceof Error ? error.message : t("optimizer.failed"),
        variant: "destructive",
      });
    } finally {
      setIsOptimizing(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
    toast({ title: t("optimizer.copied_title"), description: t("optimizer.copied_desc") });
  };

  return (
    <div className="glass-card p-4 space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 flex items-center justify-center">
          <Wand2 className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">{t("optimizer.title")}</h3>
          <p className="text-xs text-muted-foreground">{t("optimizer.subtitle")}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="relative">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleOptimize()}
            placeholder={t("optimizer.placeholder")}
            className="w-full px-4 py-3 bg-cyber-darker border border-border/30 rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 pr-28"
          />
          <button
            onClick={handleOptimize}
            disabled={isOptimizing}
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs font-medium disabled:opacity-50 transition-opacity"
          >
            {isOptimizing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {t("optimizer.button")}
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3 animate-fade-in">
          <div className="p-3 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-green-400 uppercase tracking-wider">
                {t("optimizer.optimized")}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => copyToClipboard(result.optimized)}
                  className="p-1.5 rounded hover:bg-green-500/10 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5 text-green-400" />
                </button>
                <button
                  onClick={() => window.open(`https://www.google.com/search?q=${encodeURIComponent(result.optimized)}`, "_blank")}
                  className="p-1.5 rounded hover:bg-green-500/10 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5 text-green-400" />
                </button>
              </div>
            </div>
            <code className="text-sm font-mono text-foreground break-all">{result.optimized}</code>
          </div>

          <div className="p-3 rounded-lg bg-cyber-darker border border-border/30">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5" />
              <p className="text-sm text-foreground/80">{result.explanation}</p>
            </div>
          </div>

          {result.improvements.length > 0 && (
            <div className="space-y-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {t("optimizer.improvements")}
              </span>
              <div className="flex flex-wrap gap-2">
                {result.improvements.map((imp, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 text-xs bg-violet-500/10 text-violet-400 rounded-lg border border-violet-500/20"
                  >
                    {imp}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
